import React, { useRef, useState } from "react";
import { Paperclip, X, Sparkles, PencilLine } from "lucide-react";
import { TEXTO_CON_IA, TEXTO_SIN_IA } from "@/components/documentos/PreguntaSubida";
import { toast } from "sonner";
import { hoyISO } from "@/lib/zona";

const campo = "w-full min-h-[52px] rounded-xl border border-[#E5DFD5] bg-white px-4 text-base text-[#1C2421] focus:outline-none focus:border-[#3A6B56]";

// Correo recibido pegado a mano. No se conecta a ningún buzón.
export const CorreoForm = ({ ocupado, onEnviar, onCancelar }) => {
  const [f, setF] = useState({ asunto: "", remitente: "", fecha: "", cuerpo: "" });
  const [adjunto, setAdjunto] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const elegir = (e) => {
    const a = e.target.files?.[0];
    e.target.value = "";
    if (!a) return;
    const ext = (a.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) return toast.error("Formato no admitido. Usa PDF, JPG o PNG.");
    if (a.size > 10 * 1024 * 1024) return toast.error("El archivo supera el límite de 10 MB.");
    setAdjunto(a);
  };

  const enviar = (sinIA) => {
    if (!f.asunto.trim() && !f.remitente.trim() && !f.cuerpo.trim() && !adjunto) return toast.error("Pega al menos el asunto, el texto del correo o un adjunto.");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v.trim()));
    fd.append("sin_ia", sinIA ? "si" : "no");
    if (sinIA) fd.append("confirma_no_clinico", "si");
    if (adjunto) fd.append("file", adjunto);
    onEnviar(fd, sinIA);
  };

  return (
    <div data-testid="correo-form">
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">Correo</h2>
      <p className="text-base text-[#6E7A74] mt-1">Pega un correo que hayas recibido. Rellena solo lo que tengas.</p>
      <div className="space-y-4 mt-5">
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Asunto</span>
          <input data-testid="correo-asunto" value={f.asunto} onChange={(e) => set("asunto", e.target.value)} maxLength={300} className={`${campo} mt-1.5`} /></label>
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Remitente</span>
          <input data-testid="correo-remitente" value={f.remitente} onChange={(e) => set("remitente", e.target.value)} maxLength={200}
            placeholder="Nombre o correo de quien lo envía" className={`${campo} mt-1.5`} /></label>
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Fecha del correo <span className="font-normal text-[#9AA39D]">(si la sabes)</span></span>
          <input data-testid="correo-fecha" type="date" max={hoyISO()} value={f.fecha} onChange={(e) => set("fecha", e.target.value)} className={`${campo} mt-1.5`} /></label>
        <label className="block"><span className="text-base font-semibold text-[#1C2421]">Texto del correo</span>
          <textarea data-testid="correo-cuerpo" rows={6} value={f.cuerpo} onChange={(e) => set("cuerpo", e.target.value)} maxLength={20000}
            placeholder="Pega aquí el mensaje" className={`${campo} mt-1.5 py-3 resize-y`} /></label>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={elegir} className="hidden" data-testid="correo-adjunto-input" />
          {adjunto ? (
            <div className="flex items-center gap-2 bg-white border border-[#E5DFD5] rounded-xl px-4 min-h-[52px]" data-testid="correo-adjunto">
              <Paperclip size={18} className="text-[#3A6B56] shrink-0" />
              <span className="flex-1 min-w-0 truncate text-base text-[#1C2421]">{adjunto.name}</span>
              <button data-testid="correo-adjunto-quitar" onClick={() => setAdjunto(null)} aria-label="Quitar adjunto" className="w-10 h-10 flex items-center justify-center"><X size={18} /></button>
            </div>
          ) : (
            <button data-testid="correo-adjunto-btn" onClick={() => fileRef.current?.click()} className="w-full min-h-[52px] rounded-xl border-2 border-dashed border-[#E5DFD5] bg-white font-semibold text-[#3A6B56] inline-flex items-center justify-center gap-2">
              <Paperclip size={18} /> Añadir archivo adjunto (opcional)
            </button>
          )}
        </div>
      </div>
      <p className="text-base font-semibold text-[#1C2421] mt-5">¿Cómo quieres guardarlo?</p>
      <p className="text-sm text-[#6E7A74] mt-1" data-testid="correo-transparencia-ia">No pegues informes médicos, recetas ni documentación clínica.</p>
      <div className="space-y-3 mt-3">
        {[[false, "correo-analizar", Sparkles, "Analizar con IA", TEXTO_CON_IA], [true, "correo-sin-analisis", PencilLine, "Guardar sin análisis", TEXTO_SIN_IA]].map(([sinIA, tid, I, l, d]) => (
          <button key={tid} data-testid={tid} disabled={ocupado} onClick={() => enviar(sinIA)}
            className="w-full flex items-center gap-4 bg-white border border-[#E5DFD5] rounded-2xl p-4 text-left hover:border-[#3A6B56] min-h-[72px] disabled:opacity-60">
            <span className="shrink-0 w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center"><I size={24} className="text-[#3A6B56]" /></span>
            <span><span className="block font-heading font-bold text-lg text-[#1C2421]">{l}</span><span className="block text-sm text-[#6E7A74]">{d}</span></span>
          </button>
        ))}
      </div>
      <button data-testid="correo-cancelar" onClick={onCancelar} className="w-full mt-3 min-h-[48px] rounded-xl border border-[#E5DFD5] bg-white font-semibold">Cancelar</button>
    </div>
  );
};
